import React, { useState, useContext, useRef } from 'react';
import Loading from "src/partials/loading";
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import config from '../../config';
import './ChatWidget.scss';

import { Background, Border, Color } from 'src/helpers/theme';
import { SettingsContext } from "src/context";

// Configure marked for nicer output
marked.setOptions({ gfm: true, breaks: true });
const renderer = new marked.Renderer();
renderer.link = (data) => {
  const t = data.title ? ` title="${data.title}"` : '';
  return `<a href="${data.href}"${t} target="_blank" rel="noopener noreferrer">${data.text}</a>`;
};


export default function ChatWidget({heading = 'Chat', placeholder = 'Ask a question'}) {
  const [settings, setSettings] = useContext(SettingsContext);
  const apiBase = config.base;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const botMsgIndexRef = useRef(null);
  const doStream = true;

  const toggleOpen = () => setOpen(o => !o);

  const toHtml = (md) => {
    const raw = marked.parse(md || '', { renderer });
    return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
  };

  // Minimal SSE frame parser (supports standard "data:" lines)
  const processSSEChunk = (buffer, onEvent) => {
    const events = buffer.split(/\n\n/);
    // keep last partial frame in buffer; return [remainingBuffer]
    let remainder = events.pop();
    for (const evt of events) {
      const lines = evt.split(/\n/);
      let dataLines = [];
      let eventType = 'message';
      for (const line of lines) {
        if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        else if (line.startsWith('event:')) eventType = line.slice(6).trim();
        // ignore id:, retry:, comments
      }
      const data = dataLines.join('\n');
      onEvent({ type: eventType, data });
    }
    return remainder || '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;

    // add user + placeholder bot message immediately and remember bot index
    const userMsg = { sender: 'user', text: input };
    setMessages(prev => {
      const next = [...prev, userMsg, { sender: 'bot', text: '' }];
      botMsgIndexRef.current = next.length - 1;
      return next;
    });

    const form = new FormData();
    form.append('prompt', input);
    if (doStream)
      form.append('stream', 1);
    if (sessionId) 
      form.append('session_id', sessionId);

    setInput('');

    try {
      setIsStreaming(true);
      const controller = new AbortController();
      abortRef.current = controller;

      const res = await fetch(`https://${apiBase}/mvk-api/v1/customgpt-agent/post-chat`, {
        method: 'POST',
        headers: {
          'Accept': doStream ? 'text/event-stream' : 'application/json',
        },
        body: form,
        signal: controller.signal,
      });

      // If server didn't stream SSE, fall back to JSON body
      const ctype = res.headers.get('content-type') || '';
      if (!ctype.includes('text/event-stream')) {
        const data = await res.json();
        if (data.session_id) 
          setSessionId(data.session_id);
        
        let text;
        if (!data.success) 
          text = "Sorry, an error has occurred.";
        else 
          text = data.openai_response || '';
        
        setMessages(prev => {
          const idx = botMsgIndexRef.current ?? prev.length - 1;
          const next = [...prev];
          next[idx] = { ...next[idx], text };
          return next;
        });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      // add content to last message from chtatbot
      const append = (delta) => {
        setMessages(prev => {
          const idx = botMsgIndexRef.current ?? prev.length - 1;
          const next = [...prev];
          const curr = next[idx] || { sender: 'bot', text: '' };
          next[idx] = { ...curr, text: (curr.text || '') + (delta || '') };
          return next;
        });
        // keep scrolled to bottom during stream
        setTimeout(() => {
          scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = processSSEChunk(buffer, ({ type, data }) => {
          if (!data) return;
          if (data === '[DONE]' || type === 'done') return; // ignore; stream end handled by reader
          try {
            const obj = JSON.parse(data);
            if (obj.session_id && !sessionId) 
              setSessionId(obj.session_id);
            if (obj.status && obj.status == 'error') {
              append("Sorry, an error has occurred.");
            }
            else {
              // Accept either incremental "delta" or full field "openai_response"
              const delta = typeof obj.message === 'string' ? obj.message : (obj.openai_response || '');
              if (delta) append(delta);
            }
          } catch {
            // If not JSON, treat as raw text delta
            append(data);
          }
        });
      }
    } catch (err) {
      console.error(err);
      setMessages(prev => {
        const idx = botMsgIndexRef.current ?? prev.length - 1;
        const next = [...prev];
        next[idx] = { sender: 'bot', text: 'Sorry, something went wrong.' };
        return next;
      });
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      setTimeout(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  };

  const handleStop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  
  let siteButtonStyle = settings.mvk_theme_config.other?.primary_button_style;
  let buttonStyle = {
      fontFamily: `${settings.design?.fonts.body.body_google_font_name}, sans-serif`,
      backgroundColor: Background("light", siteButtonStyle),
      color: Color("light", siteButtonStyle),
      borderRadius: (settings.mvk_theme_config.other?.enable_border_radius) ? `${settings.mvk_theme_config.other?.border_radius_size}px` : "0px",
      border: Border("light", siteButtonStyle),
      fontWeight: settings.mvk_theme_config.other?.button_font_weight,
      transition: '.3s'
  };

  let bubbleStyle = {
      "--user-bg-color": Background("light", siteButtonStyle)
  };

  return (
    <>
      <button className="chat-button" style={buttonStyle} onClick={toggleOpen}>
        {open ? 'Close' : 'Chat'}
      </button>

      <div className={`chat-container ${open ? 'open' : ''}`}>
        <div className="chat-header">
          <h2>{heading}</h2>
          <button className="chat-header-close" onClick={toggleOpen}>&times;</button>
        </div>
        <div className="chat-messages" style={bubbleStyle}>
          {messages.map((msg, i) => (
            <div key={i} className={`chat-message ${msg.sender}`} dangerouslySetInnerHTML={{ __html: toHtml(msg.text) }}>
            </div>
          ))}
          <div ref={scrollRef} />
        </div>
        <form className="chat-form" onSubmit={handleSubmit}>
          <input className="chat-input" name="chat-input" value={input} onChange={e => setInput(e.target.value)} placeholder={placeholder} />
          <button type="submit" className="chat-send-button" style={buttonStyle} disabled={isStreaming}>
            {isStreaming ? <Loading type="dot-pulse" dotColor="#fff" /> : 'Send'}
          </button>
        </form>
      </div>
    </>
  );
}
